/**
 * v130 — Active range-session lifecycle hook.
 *
 * What's an "active session"? While the user is at the range, they want
 * a 3-5 second per-shot capture loop. The active session is the
 * in-progress container that accumulates RichShots before the user
 * commits the session to the permanent log (which goes through useRange).
 *
 * Persistence: localStorage 'sparrow.rangeLog.activeSession.v2'. Survives
 * refresh — the user's phone backgrounds, screen sleeps, browser killed,
 * whatever. Active session is kept on disk; only commit when "End session"
 * is tapped.
 *
 * Storage is sandbox-safe (same shim pattern as useRange).
 */

import { useCallback, useEffect, useState } from 'react';
import type {
  RichShot,
  SessionConditions,
  SessionSetupSnapshot,
} from './v2-schema';
import type { ShotSet, PhotoCalibration } from './v3-schema';
import { V132_KEYS } from './v3-schema';

const ACTIVE_KEY = V132_KEYS.activeSessionV3;
const ACTIVE_KEY_V2_FALLBACK = V132_KEYS.activeSessionV2;

export interface ActiveSession {
  /** ISO timestamp the session started. */
  startedAt: string;
  /** Optional name the user typed at start. */
  location?: string;
  /** Shots logged so far. */
  shots: RichShot[];
  /** Session-level environmental snapshot. */
  conditions?: SessionConditions;
  /** Frozen setup at session start. */
  setupSnapshot?: SessionSetupSnapshot;
  /** Last distance the user picked — for "stay on this distance" UX. */
  lastDistanceYd?: number;
  /** Last pin used — for fast repeat shots at the same distance. */
  lastPin?: string;
  /** v132 — sets within the active session. Always at least one set after
   *  the first shot is logged. The last set is the open one. */
  sets?: ShotSet[];
}

export interface UseActiveSession {
  active: ActiveSession | null;
  /** Begin a new session. Replaces any in-progress one (call ended first). */
  begin: (opts: {
    location?: string;
    conditions?: SessionConditions;
    setupSnapshot?: SessionSetupSnapshot;
    initialDistanceYd?: number;
  }) => void;
  /** Add a shot and persist. Auto-appends to the open set; creates the
   *  first set on the first shot. */
  addShot: (shot: Omit<RichShot, 'id' | 'shotAt'>) => RichShot;
  /** Remove a shot (e.g. mis-tap). Also removes its id from the owning set. */
  removeShot: (id: string) => void;
  /** Update session-level fields (location / conditions). */
  patch: (p: Partial<Pick<ActiveSession, 'location' | 'conditions' | 'lastDistanceYd' | 'lastPin'>>) => void;
  /** v132 — Start a new set. Closes the current open set (sets `endedAt`)
   *  and opens a new one with the provided label / setupDelta / notes. */
  startNewSet: (opts: { label?: string; setupDelta?: string; notes?: string }) => void;
  /** v132 — Edit the open (current) set's metadata. */
  patchCurrentSet: (p: Partial<Pick<ShotSet, 'label' | 'setupDelta' | 'notes'>>) => void;
  /** v133 — Attach a photo + calibration to the open set. */
  setSetPhoto: (p: { photoDataUri: string; calibration: PhotoCalibration }) => void;
  /** End and clear the active session. Caller is responsible for committing
   *  to useRange BEFORE calling end() if they want to keep the shots. */
  end: () => void;
}

function safeGetItem(key: string): string | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(key, value);
  } catch { /* ignore */ }
}

function safeRemoveItem(key: string): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(key);
  } catch { /* ignore */ }
}

function loadActive(): ActiveSession | null {
  // Prefer v3; fall back to v2 (read-only — first write will promote to v3 key).
  const raw = safeGetItem(ACTIVE_KEY) ?? safeGetItem(ACTIVE_KEY_V2_FALLBACK);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as ActiveSession;
    if (!Array.isArray(parsed.shots)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function genShotId(): string {
  return `sh_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}
function genSetId(): string {
  return `set_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function useActiveSession(): UseActiveSession {
  const [active, setActive] = useState<ActiveSession | null>(() => loadActive());

  useEffect(() => {
    if (active) safeSetItem(ACTIVE_KEY, JSON.stringify(active));
    else safeRemoveItem(ACTIVE_KEY);
  }, [active]);

  const begin = useCallback<UseActiveSession['begin']>((opts) => {
    setActive({
      startedAt: new Date().toISOString(),
      location: opts.location,
      shots: [],
      conditions: opts.conditions,
      setupSnapshot: opts.setupSnapshot,
      lastDistanceYd: opts.initialDistanceYd,
    });
  }, []);

  const addShot = useCallback<UseActiveSession['addShot']>((s) => {
    const shot: RichShot = {
      ...s,
      id: genShotId(),
      shotAt: new Date().toISOString(),
    };
    setActive((prev) => {
      if (!prev) return prev;
      // Ensure there's at least one open set; create one on first shot.
      const sets = ensureOpenSet(prev.sets, prev.startedAt);
      const last = sets[sets.length - 1];
      const updatedLast: ShotSet = { ...last, shotIds: [...last.shotIds, shot.id] };
      return {
        ...prev,
        shots: [...prev.shots, shot],
        sets: [...sets.slice(0, -1), updatedLast],
        lastDistanceYd: s.distanceYd,
        lastPin: s.pin,
      };
    });
    return shot;
  }, []);

  const removeShot = useCallback<UseActiveSession['removeShot']>((id) => {
    setActive((prev) => {
      if (!prev) return prev;
      const shots = prev.shots.filter((s) => s.id !== id);
      const sets = prev.sets
        ? prev.sets.map((set) => ({ ...set, shotIds: set.shotIds.filter((sid) => sid !== id) }))
        : prev.sets;
      return { ...prev, shots, sets };
    });
  }, []);

  const startNewSet = useCallback<UseActiveSession['startNewSet']>((opts) => {
    setActive((prev) => {
      if (!prev) return prev;
      const now = new Date().toISOString();
      const sets = (prev.sets ?? []).slice();
      // Close the open set if any
      if (sets.length > 0 && !sets[sets.length - 1].endedAt) {
        sets[sets.length - 1] = { ...sets[sets.length - 1], endedAt: now };
      }
      sets.push({
        id: genSetId(),
        label: opts.label,
        setupDelta: opts.setupDelta,
        notes: opts.notes,
        startedAt: now,
        shotIds: [],
      });
      return { ...prev, sets };
    });
  }, []);

  const patchCurrentSet = useCallback<UseActiveSession['patchCurrentSet']>((p) => {
    setActive((prev) => {
      if (!prev || !prev.sets || prev.sets.length === 0) return prev;
      const last = prev.sets[prev.sets.length - 1];
      const updated: ShotSet = { ...last, ...p };
      return { ...prev, sets: [...prev.sets.slice(0, -1), updated] };
    });
  }, []);

  const setSetPhoto = useCallback<UseActiveSession['setSetPhoto']>((p) => {
    setActive((prev) => {
      if (!prev) return prev;
      const sets = ensureOpenSet(prev.sets, prev.startedAt);
      const last = sets[sets.length - 1];
      const updated: ShotSet = {
        ...last,
        photoDataUri: p.photoDataUri,
        photoCalibration: p.calibration,
      };
      return { ...prev, sets: [...sets.slice(0, -1), updated] };
    });
  }, []);

  const patch = useCallback<UseActiveSession['patch']>((p) => {
    setActive((prev) => prev ? { ...prev, ...p } : prev);
  }, []);

  const end = useCallback(() => {
    setActive(null);
  }, []);

  return { active, begin, addShot, removeShot, patch, startNewSet, patchCurrentSet, setSetPhoto, end };
}

/** Ensure there's at least one open set. Returns a fresh sets array. */
function ensureOpenSet(sets: ShotSet[] | undefined, sessionStartedAt: string): ShotSet[] {
  if (sets && sets.length > 0 && !sets[sets.length - 1].endedAt) return sets;
  const newSet: ShotSet = {
    id: genSetId(),
    label: sets && sets.length > 0 ? `Set ${sets.length + 1}` : 'Set 1',
    startedAt: sets && sets.length > 0 ? new Date().toISOString() : sessionStartedAt,
    shotIds: [],
  };
  return [...(sets ?? []), newSet];
}
